import React from 'react';
import { Box, Tabs, Tab, IconButton, Typography, Divider } from '@mui/material';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import ImageIcon from '@mui/icons-material/Image';
import QrCodeIcon from '@mui/icons-material/QrCode';
import DynamicFormIcon from '@mui/icons-material/DynamicForm';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import TuneIcon from '@mui/icons-material/Tune';
import SettingsIcon from '@mui/icons-material/Settings';
import { ElementType, DesignElement, CouponDesign, GridSettings } from '../../../types/coupon';
import { ElementProperties } from './ElementProperties';
import { GlobalSettings } from './GlobalSettings';

export interface DesignerToolbarProps {
    activeTab: number;
    onTabChange: (newValue: number) => void;
    onAddElement: (type: ElementType) => void;
    selectedElement?: DesignElement;
    onElementUpdate: (element: DesignElement) => void;
    design: CouponDesign;
    onDesignUpdate: (design: CouponDesign) => void;
    gridSettings: GridSettings;
    onGridSettingsUpdate: (settings: GridSettings) => void;
}

const tools = [
    { type: 'text' as ElementType, icon: <TextFieldsIcon />, label: 'Text' },
    { type: 'image' as ElementType, icon: <ImageIcon />, label: 'Image' },
    { type: 'qrcode' as ElementType, icon: <QrCodeIcon />, label: 'QR Code' },
    { type: 'dynamic' as ElementType, icon: <DynamicFormIcon />, label: 'Dynamic Field' },
];

const tabConfig = [
    { label: 'Elements', icon: <ViewModuleIcon />, disabled: false },
    { label: 'Properties', icon: <TuneIcon />, disabled: (props: DesignerToolbarProps) => !props.selectedElement },
    { label: 'Global', icon: <SettingsIcon />, disabled: false }
];

export const DesignerToolbar: React.FC<DesignerToolbarProps> = ({
    activeTab,
    onTabChange,
    onAddElement,
    selectedElement,
    onElementUpdate,
    design,
    onDesignUpdate,
    gridSettings,
    onGridSettingsUpdate,
}) => {
    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Tab Navigation */}
            <Tabs 
                value={activeTab}
                onChange={(_, newValue) => onTabChange(newValue)}
                variant="fullWidth"
                sx={{
                    minHeight: 48,
                    borderBottom: 1,
                    borderColor: 'divider',
                    '& .MuiTab-root': {
                        minHeight: 48,
                        minWidth: 'auto',
                        padding: '6px 12px',
                        flex: 1
                    }
                }}
            >
                {tabConfig.map((tab, index) => (
                    <Tab
                        key={tab.label}
                        disabled={typeof tab.disabled === 'function' ? tab.disabled({ 
                            activeTab, onTabChange, onAddElement, selectedElement, 
                            onElementUpdate, design, onDesignUpdate, gridSettings, 
                            onGridSettingsUpdate 
                        }) : tab.disabled}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '4px',
                            '& .MuiSvgIcon-root': {
                                fontSize: '1.5rem',
                                mb: 0.5
                            },
                            '& .tab-label': {
                                fontSize: '0.675rem',
                                lineHeight: 1,
                                textTransform: 'none',
                                letterSpacing: 0
                            }
                        }}
                        icon={tab.icon}
                        label={<span className="tab-label">{tab.label}</span>}
                    />
                ))}
            </Tabs>

            {/* Tab Content */}
            <Box sx={{ 
                flex: 1, 
                overflowY: 'auto',
                p: 2,
                bgcolor: '#ffffff'
            }}>
                {activeTab === 0 && (
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                        {tools.map((tool) => (
                            <Box
                                key={tool.type}
                                onClick={() => onAddElement(tool.type)}
                                sx={{
                                    p: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: 1,
                                    cursor: 'pointer',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    bgcolor: '#f8fafc',
                                    color: '#637381',
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                        bgcolor: '#f1f5f9',
                                        borderColor: '#2065D1',
                                        color: '#2065D1'
                                    }
                                }}
                            >
                                {tool.icon}
                                <Typography variant="caption" sx={{ fontWeight: 500 }}>
                                    {tool.label}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                )}

                {activeTab === 1 && selectedElement && (
                    <ElementProperties 
                        element={selectedElement}
                        onUpdate={onElementUpdate}
                        currentBreakpoint="desktop"
                    />
                )}

                {activeTab === 2 && (
                    <GlobalSettings
                        design={design}
                        onDesignUpdate={onDesignUpdate}
                        gridSettings={gridSettings}
                        onGridSettingsUpdate={onGridSettingsUpdate}
                    />
                )}
            </Box>
        </Box>
    );
};
